import { put, call, takeLatest, select } from "redux-saga/effects";
import * as types from "../constants/prayerFeatureActionTypes";
import {
    client, registerSession, joinCommunity, subscribe, addUserToModeratorRole, banUser,
    queryAllPosts, queryPrayerOfTheWeek, queryMyPosts, postPrayer, editPost, deletePrayer, notifyDeletedPost, addReaction, removeReaction,
    queryAllComments, getComment, addComment, deleteComment, editComment, addCommentReaction, removeCommentReaction, getUsersDeletedPosts
} from "../api/prayerFeatureApi";
import { ErrorCode } from '@amityco/js-sdk';
import * as tools from "../helpers/Tools";
import { logUI } from "../api/andyliticsApi";

export default function* prayerFeatureSaga() {
    yield takeLatest(types.SETUP_AMITY_USER_SESSION, setupUserSession);
    yield takeLatest(types.SUBSCRIBE_TO_COMMUNITY, subscribeToCommunity);
    yield takeLatest(types.GET_PRAYER_OF_THE_WEEK, handlePrayerOfTheWeek);
    yield takeLatest(types.GET_ALL_PRAYERS, handleQueryAllPosts);
    yield takeLatest(types.GET_ALL_PRAYERS_SUCCESS, handleQueryAllPostsSuccess);
    yield takeLatest(types.GET_MY_PRAYERS, handleQueryMyPosts);
    yield takeLatest(types.REFRESH_ALL_PRAYERS, handleRefreshAllPrayers);
    yield takeLatest(types.REFRESH_MY_PRAYERS, handleRefreshMyPrayers);
    yield takeLatest(types.GET_NEXT_PRAYERS, handleNextPrayers);
    yield takeLatest(types.POST_PRAYER, handlePostPrayer);
    yield takeLatest(types.EDIT_PRAYER, handleEditPrayer);
    yield takeLatest(types.DELETE_PRAYER, handleDeletePrayer);
    yield takeLatest(types.NOTIFY_DELETED_PRAYER, handleNotifyDeletedPrayer);
    yield takeLatest(types.BAN_USER, handleBanUser);
    yield takeLatest(types.ADD_PRAYER_REACTION, handleAddReaction);
    yield takeLatest(types.REMOVE_PRAYER_REACTION, handleRemoveReaction);
    yield takeLatest(types.GET_COMMENTS, handleGetComments);
    yield takeLatest(types.GET_COMMENT, handleGetComment);
    yield takeLatest(types.ADD_COMMENT, handleAddComment);
    yield takeLatest(types.EDIT_COMMENT, handleEditComment);
    yield takeLatest(types.DELETE_COMMENT, handleDeleteComment);
    yield takeLatest(types.ADD_COMMENT_REACTION, handleAddCommentReaction);
    yield takeLatest(types.REMOVE_COMMENT_REACTION, handleRemoveCommentReaction);
    yield takeLatest(types.GET_USERS_DELETED_POSTS, handleUsersDeletedPosts);
}

function* isConnected() {
    const prayerFeature = yield select(state => state.prayerFeature);
    const { connectionState, livePrayers } = prayerFeature;
    return !window.isOffline && (connectionState === 'connected' || (connectionState === null && livePrayers && livePrayers.loadingStatus !== 'error'));
};

export function* setupUserSession(payload) {
    let response = null;
    let error = null;
    const memberId = payload.memberId;
    try {
        logUI(types.SETUP_AMITY_USER_SESSION, new Date(), tools.getURLPath(), null, memberId);
        let token = payload.token;
        if (token && typeof token === 'string') {
            response = yield call(registerSession, memberId, token);
            console.log("amity-registerSession: ", response);
            //const communityHasBeenJoined = yield call(joinCommunity);
            //console.log('communityHasBeenJoined', communityHasBeenJoined);
            yield put({ type: types.SETUP_AMITY_USER_SESSION_SUCCESS, token, memberId: payload.memberId });
            logUI(types.SETUP_AMITY_USER_SESSION_SUCCESS, new Date(), tools.getURLPath(), { token }, memberId);
        }
        else {
            error = 'empty or unknown token';
            yield put({ type: types.SETUP_AMITY_USER_SESSION_FAILURE, error });
            logUI(types.SETUP_AMITY_USER_SESSION_FAILURE, new Date(), tools.getURLPath(), { error }, memberId);
        }
    }
    catch (err) {
        error = err ? err : 'failed to get token';
        yield put({ type: types.SETUP_AMITY_USER_SESSION_FAILURE, error });
        logUI(types.SETUP_AMITY_USER_SESSION_FAILURE, new Date(), tools.getURLPath(), { error }, memberId);
    }
    finally {
        if (payload.callback) {
            payload.callback(response, error);
        }
    }
}

export function* subscribeToCommunity(payload) {
    const communityHasBeenJoined = yield call(joinCommunity);
    console.log('communityHasBeenJoined', communityHasBeenJoined);
    const communityTopic = yield call(subscribe);
    if (payload.callback) {
        payload.callback(communityTopic);
    }
}

function resetLiveObject(liveObject) {
    if (liveObject) {
        liveObject.loadingStatus = undefined;
        if (liveObject.resetPage) {
            liveObject.resetPage();
        }
    }
}

function onOnline() {
    window.isOffline = false;
    //    const prayerFeature = sessionStorage['root'] ? JSON.parse(JSON.parse(sessionStorage['root']).prayerFeature) : window.store.getState();
    //    const { livePrayers, liveMyPrayers } = prayerFeature;
    //    resetLiveObject(livePrayers);
    //    resetLiveObject(liveMyPrayers);
}

function onOffline() {
    window.isOffline = true;
}

window.addEventListener('online', onOnline);
window.addEventListener('offline', onOffline);

export function* cleanupUserSession() {
    const prayerFeature = yield select(state => state.prayerFeature);
    if (prayerFeature.livePrayers) {
        prayerFeature.livePrayers.removeAllListeners('dataUpdated');
        prayerFeature.livePrayers.dispose();
    }
    if (prayerFeature.liveMyPrayers) {
        prayerFeature.liveMyPrayers.removeAllListeners('dataUpdated');
        prayerFeature.liveMyPrayers.dispose();
    }
    client.unregisterSession();
    client.removeAllListeners('connectionStatusChanged');
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    yield put({ type: types.RESET_PRAYER_FEATURE });
    logUI(types.RESET_PRAYER_FEATURE, new Date(), tools.getURLPath(), null, prayerFeature.memberId);
}

export function* handleUserRole(data) {
    if (data.userRole !== 'moderator') {
        const sso = yield select(state => state.sso);
        const { userDetails } = sso;
        const response = yield call(addUserToModeratorRole, userDetails.access_token);
        console.log('addUserToModeratorRole', response);
    }
}

export function* handlePrayerOfTheWeek(data) {
    let response = null;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.GET_PRAYER_OF_THE_WEEK, new Date(), tools.getURLPath(), null, prayerFeature.memberId);
        response = yield call(queryPrayerOfTheWeek);
    }
    catch (error) {
        yield put({ type: types.GET_PRAYER_OF_THE_WEEK_FAILURE });
        console.log(error);
        logUI(types.GET_PRAYER_OF_THE_WEEK_FAILURE, new Date(), tools.getURLPath(), { error }, prayerFeature.memberId);
    }
    finally {
        if (data && data.callback) {
            data.callback(response);
        }
    }
}

export function* handleQueryAllPosts(data) {
    let response = null;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.GET_ALL_PRAYERS, new Date(), tools.getURLPath(), null, prayerFeature.memberId);
        response = yield call(queryAllPosts);
    }
    catch (error) {
        yield put({ type: types.GET_ALL_PRAYERS_FAILURE });
        console.log(error);
        logUI(types.GET_ALL_PRAYERS_FAILURE, new Date(), tools.getURLPath(), { error }, prayerFeature.memberId);
    }
    finally {
        if (data && data.callback) {
            data.callback(response);
        }
    }
}

export function* handleQueryAllPostsSuccess(data) {
    const prayerFeature = yield select(state => state.prayerFeature);
    const prayers = data && data.allPrayers;
    const gotNewPost = prayers && prayers.length > 0 && prayers[0].createdAt > prayerFeature.lastLoadTime;
    yield put({ type: types.UPDATE_LAST_LOAD_TIME });
    logUI(types.UPDATE_LAST_LOAD_TIME, new Date(), tools.getURLPath(), { gotNewPost }, prayerFeature.memberId);
    if (data && data.callback) {
        data.callback(gotNewPost);
    }
}

export function* handleQueryMyPosts(data) {
    let response = null;
    try {
        logUI(types.GET_MY_PRAYERS, new Date(), tools.getURLPath(), null, data.memberId);
        response = yield call(queryMyPosts, data.memberId);
    }
    catch (error) {
        yield put({ type: types.GET_MY_PRAYERS_FAILURE });
        logUI(types.GET_MY_PRAYERS_FAILURE, new Date(), tools.getURLPath(), null, data.memberId);
        console.log(error);
    }
    finally {
        if (data && data.callback) {
            data.callback(response);
        }
    }
}

export function* handleRefreshAllPrayers(data) {
    if (yield isConnected()) {
        yield call(handleQueryAllPosts, data);
    }
}

export function* handleRefreshMyPrayers(data) {
    if (yield isConnected()) {
        yield call(handleQueryMyPosts, data);
    }
}

export function* handleNextPrayers(data) {
    let gotMore = false;
    let error = null;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        if (yield isConnected()) {
            const { activeTab, liveMyPrayers } = prayerFeature;
            const livePrayers = (activeTab === '1' && prayerFeature.livePrayers) || (activeTab === '2' && liveMyPrayers) || null;
            if (livePrayers && livePrayers.hasMore && livePrayers.loadingStatus === 'loaded') {
                livePrayers.nextPage();
                gotMore = true;
            }
        }
        else {
            error = 'not connected';
        }
    }
    catch (e) {
        error = e || 'got error';
    }
    finally {
        logUI(types.GET_NEXT_PRAYERS + (error ? ".Failure" : ".Success"), new Date(), tools.getURLPath(), { gotMore, error }, prayerFeature.memberId);
        yield put({ type: types.GET_NEXT_PRAYERS_COMPLETED, gotMore });
        if (data.callback) {
            data.callback(gotMore, error);
        }
    }
}

export function* handlePostPrayer(payload) {
    let response = null;
    let success = false;
    const prayerFeature = yield select(state => state.prayerFeature);
    const post = payload.prayer;
    try {
        logUI(types.POST_PRAYER, new Date(), tools.getURLPath(), post, prayerFeature.memberId, post.topics[0]);
        response = yield call(postPrayer, post, payload.memberId);
        yield put({ type: types.POST_PRAYER_SUCCESS, myRecentPrayerPostId: response.postId, topic: post.topics[0] });
        success = true;
        logUI(types.POST_PRAYER_SUCCESS, new Date(), tools.getURLPath(), response, prayerFeature.memberId, post.topics[0]);
    }
    catch (error) {
        console.log('post prayer error:', error);
        yield put({ type: types.POST_PRAYER_FAILURE, error: error ?? 'failed to post prayer', hasBanWord: error.code === ErrorCode.BanWordFound });
        logUI(types.POST_PRAYER_FAILURE, new Date(), tools.getURLPath(), { ...post, hasBanWord: error.code === ErrorCode.BanWordFound }, prayerFeature.memberId, post.topics[0]);
    }
    finally {
        if (payload.callback) {
            payload.callback(response, !success);
        }
    }
}

export function* handleEditPrayer(payload) {
    let response = null;
    let success = false;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.EDIT_PRAYER, new Date(), tools.getURLPath(), payload, prayerFeature.memberId, payload.postId);
        response = yield call(editPost, payload.postId, payload.textInput, payload.memberId);
        success = true;
        console.log('edit prayer response:', response);
        yield put({ type: types.EDIT_PRAYER_SUCCESS, myEditPrayerPostId: response && response.postId, textInput: response && response.textInput });
        logUI(types.EDIT_PRAYER_SUCCESS, new Date(), tools.getURLPath(), { response }, prayerFeature.memberId, payload.postId);
    }
    catch (error) {
        console.log('edit prayer error:', error);
        yield put({ type: types.EDIT_PRAYER_FAILURE, error: error ?? 'failed to edit prayer', hasBanWord: error.code === ErrorCode.BanWordFound });
        logUI(types.EDIT_PRAYER_FAILURE, new Date(), tools.getURLPath(), { ...payload, hasBanWord: error.code === ErrorCode.BanWordFound }, prayerFeature.memberId, payload.postId);
    }
    finally {
        if (payload.callback) {
            payload.callback(response, !success);
        }
    }
}

export function* handleDeletePrayer(payload) {
    let success = false;
    const post = payload.prayer;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.DELETE_PRAYER, new Date(), tools.getURLPath(), null, prayerFeature.memberId, post.postId);
        success = yield call(deletePrayer, post.postId);
        if (success) {
            yield put({ type: types.DELETE_PRAYER_SUCCESS, prayer: post, reasonForDelete: payload.reasonForDelete });
        } else {
            yield put({ type: types.DELETE_PRAYER_FAILURE });
        }
        logUI(success ? types.DELETE_PRAYER_SUCCESS : types.DELETE_PRAYER_FAILURE, new Date(), tools.getURLPath(), null, prayerFeature.memberId, post.postId);
    }
    catch (error) {
        yield put({ type: types.DELETE_PRAYER_FAILURE, error });
        logUI(types.DELETE_PRAYER_FAILURE, new Date(), tools.getURLPath(), error, prayerFeature.memberId, post.postId);
    }
    finally {
        if (payload.callback) {
            payload.callback(success);
        }
    }
}

export function* handleNotifyDeletedPrayer(payload) {
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        const post = payload.prayer;
        const data = JSON.parse(post.data.text);
        logUI(types.NOTIFY_DELETED_PRAYER, new Date(), tools.getURLPath(), null, prayerFeature.memberId, post.postId);
        const result = yield call(
            notifyDeletedPost, post.postedUserId, post.postId, data.title, data.text,
            post.createdAt, prayerFeature.memberId, 'moderator', payload.reasonForDelete
        );
        yield put({ type: types.NOTIFY_DELETED_PRAYER_SUCCESS, result });
        logUI(types.NOTIFY_DELETED_PRAYER_SUCCESS, new Date(), tools.getURLPath(), { result }, prayerFeature.memberId, post.postId);
    }
    catch (error) {
        yield put({ type: types.NOTIFY_DELETED_PRAYER_FAILURE, error });
        logUI(types.NOTIFY_DELETED_PRAYER_FAILURE, new Date(), tools.getURLPath(), { error }, prayerFeature.memberId, post.postId);
    }
}

export function* handleBanUser(payload) {
    let response = null;
    let error = null;
    const userId = payload && payload.userId;
    try {
        if (userId) {
            logUI(types.BAN_USER, new Date(), tools.getURLPath(), null, userId);
            response = yield call(banUser, userId);
            if (response && response.status == 'error') {
                error = response;
                response = null;
                yield put({ type: types.BAN_USER_FAILURE, error });
                logUI(types.BAN_USER_FAILURE, new Date(), tools.getURLPath(), { error }, userId);
            }
            else {
                yield put({ type: types.BAN_USER_SUCCESS, response });
                logUI(types.BAN_USER_SUCCESS, new Date(), tools.getURLPath(), { response }, userId);
            }
        }
    }
    catch (err) {
        error = err;
        yield put({ type: types.BAN_USER_FAILURE, error });
        logUI(types.BAN_USER_FAILURE, new Date(), tools.getURLPath(), { error }, userId);
    }
    finally {
        if (payload.callback) {
            payload.callback(response, error);
        }
    }
}

export function* handleAddReaction(payload) {
    let success = false;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.ADD_PRAYER_REACTION, new Date(), tools.getURLPath(), null, prayerFeature.memberId, payload.postId);
        success = yield call(addReaction, payload.postId);
        yield put({ type: types.ADD_PRAYER_REACTION_SUCCESS, success: success, postId: payload.postId });
        logUI(success ? types.ADD_PRAYER_REACTION_SUCCESS : types.ADD_PRAYER_REACTION_FAILURE, new Date(), tools.getURLPath(), null, prayerFeature.memberId, payload.postId);
    }
    catch (error) {
        yield put({ type: types.ADD_PRAYER_REACTION_FAILURE, error });
        console.log('add prayer reaction failed', error);
        logUI(types.ADD_PRAYER_REACTION_FAILURE, new Date(), tools.getURLPath(), payload, prayerFeature.memberId);
    }
    finally {
        if (payload.callback) {
            payload.callback(success);
        }
    }
}

export function* handleRemoveReaction(payload) {
    let success = false;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.REMOVE_PRAYER_REACTION, new Date(), tools.getURLPath(), null, prayerFeature.memberId, payload.postId);
        success = yield call(removeReaction, payload.postId);
        yield put({ type: types.REMOVE_PRAYER_REACTION_SUCCESS, success: success, postId: payload.postId });
        logUI(success ? types.REMOVE_PRAYER_REACTION_SUCCESS : types.REMOVE_PRAYER_REACTION_FAILURE, new Date(), tools.getURLPath(), null, prayerFeature.memberId, payload.postId);
    }
    catch (error) {
        yield put({ type: types.REMOVE_PRAYER_REACTION_FAILURE, error });
        console.log('remove prayer reaction failed', error);
        logUI(types.REMOVE_PRAYER_REACTION_FAILURE, new Date(), tools.getURLPath(), payload, prayerFeature.memberId);
    }
    finally {
        if (payload.callback) {
            payload.callback(success);
        }
    }
}

export function* handleGetComments(payload) {
    try {
        const prayerFeature = yield select(state => state.prayerFeature);
        logUI(types.GET_COMMENTS, new Date(), tools.getURLPath(), null, prayerFeature.memberId, payload.postId);
        const liveObject = yield call(queryAllComments, payload.postId);
        yield put({ type: types.GET_COMMENTS_SUCCESS, postId: payload.postId });
        logUI(types.GET_COMMENTS_SUCCESS, new Date(), tools.getURLPath(), null, prayerFeature.memberId);
        payload.callback(liveObject);
    }
    catch (error) {
        yield put({ type: types.GET_COMMENTS_FAILURE, error });
        logUI(types.GET_COMMENTS_FAILURE, new Date(), tools.getURLPath(), { error, payload }, prayerFeature.memberId);
    }
}

export function* handleGetComment(payload) {
    try {
        const prayerFeature = yield select(state => state.prayerFeature);
        logUI(types.GET_COMMENT, new Date(), tools.getURLPath(), payload, prayerFeature.memberId, payload.commentId);
        const liveObject = yield call(getComment, payload.commentId);
        yield put({ type: types.GET_COMMENT_SUCCESS, commentId: payload.commentId });
        logUI(types.GET_COMMENT_SUCCESS, new Date(), tools.getURLPath(), payload, prayerFeature.memberId);
        payload.callback(liveObject);
    }
    catch (error) {
        yield put({ type: types.GET_COMMENT_FAILURE, error });
        logUI(types.GET_COMMENT_FAILURE, new Date(), tools.getURLPath(), { error, payload }, prayerFeature.memberId);
    }
}

export function* handleAddComment(payload) {
    let response = null;
    let success = false;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.ADD_COMMENT, new Date(), tools.getURLPath(), payload, prayerFeature.memberId, payload.postId);
        const sso = yield select(state => state.sso);
        const { userDetails } = sso;
        response = yield call(addComment, payload.comment, payload.postId, payload.postUserId, userDetails.Email);
        success = true;
        yield put({ type: types.ADD_COMMENT_SUCCESS, comment: response });
        logUI(types.ADD_COMMENT_SUCCESS, new Date(), tools.getURLPath(), response, prayerFeature.memberId, payload.postId);
    }
    catch (error) {
        yield put({ type: types.ADD_COMMENT_FAILURE, error: error, commentHasBanWord: error.code === ErrorCode.BanWordFound });
        logUI(types.ADD_COMMENT_FAILURE, new Date(), tools.getURLPath(), { ...payload, hasBanWor: error.code === ErrorCode.BanWordFound }, prayerFeature.memberId);
    }
    finally {
        if (payload.callback) {
            payload.callback(response, !success);
        }
    }
}

export function* handleEditComment(payload) {
    let response = null;
    let success = false;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.EDIT_COMMENT, new Date(), tools.getURLPath(), payload, prayerFeature.memberId);
        response = yield call(editComment, payload.commentId, payload.textInput);
        success = true;
        yield put({ type: types.EDIT_COMMENT_SUCCESS, comment: response });
        logUI(types.EDIT_COMMENT_SUCCESS, new Date(), tools.getURLPath(), response, prayerFeature.memberId);
    }
    catch (error) {
        console.log(error);
        yield put({ type: types.EDIT_COMMENT_FAILURE, error: error ?? 'failed to edit comment', hasBanWord: error.code === ErrorCode.BanWordFound });
        logUI(types.EDIT_COMMENT_FAILURE, new Date(), tools.getURLPath(), { ...payload, hasBanWor: error.code === ErrorCode.BanWordFound }, prayerFeature.memberId);
    }
    finally {
        if (payload.callback) {
            payload.callback(response, !success);
        }
    }
}

export function* handleDeleteComment(payload) {
    let success = false;
    const comment = payload.comment;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.DELETE_COMMENT, new Date(), tools.getURLPath(), comment, prayerFeature.memberId, comment.referenceId);
        success = yield call(deleteComment, comment.commentId);
        console.log('delete comment result: ', success);
        if (success) {
            yield put({ type: types.DELETE_COMMENT_SUCCESS, comment });
        } else {
            yield put({ type: types.DELETE_COMMENT_FAILURE });
        }
        logUI(success ? types.DELETE_COMMENT_SUCCESS : types.DELETE_COMMENT_FAILURE, new Date(), tools.getURLPath(), comment, prayerFeature.memberId, comment.referenceId);
    }
    catch (error) {
        console.log('delete comment error: ', error);
        yield put({ type: types.DELETE_COMMENT_FAILURE, error });
        logUI(types.DELETE_COMMENT_FAILURE, new Date(), tools.getURLPath(), { payload, error }, prayerFeature.memberId, comment.referenceId);
    }

    finally {
        if (payload.callback) {
            payload.callback(success);
        }
    }
}

export function* handleAddCommentReaction(payload) {
    let success = false;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.ADD_COMMENT_REACTION, new Date(), tools.getURLPath(), payload, prayerFeature.memberId, payload.postId);
        success = yield call(addCommentReaction, payload.commentId);
        yield put({ type: types.ADD_COMMENT_REACTION_SUCCESS, success: success, postId: payload.postId });
        logUI(types.ADD_COMMENT_REACTION_SUCCESS, new Date(), tools.getURLPath(), { ...payload, success }, prayerFeature.memberId, payload.postId);
    }
    catch (error) {
        yield put({ type: types.ADD_COMMENT_REACTION_FAILURE, error });
        console.log('add comment reaction failed', error);
        logUI(types.ADD_COMMENT_REACTION_FAILURE, new Date(), tools.getURLPath(), { ...payload, error }, prayerFeature.memberId);
    }
    finally {
        if (payload.callback) {
            payload.callback(success);
        }
    }
}

export function* handleRemoveCommentReaction(payload) {
    let success = false;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.REMOVE_COMMENT_REACTION, new Date(), tools.getURLPath(), payload, prayerFeature.memberId, payload.postId);
        success = yield call(removeCommentReaction, payload.commentId);
        yield put({ type: types.REMOVE_COMMENT_REACTION_SUCCESS, success: success, postId: payload.postId });
        logUI(types.REMOVE_COMMENT_REACTION_SUCCESS, new Date(), tools.getURLPath(), { ...payload, success }, prayerFeature.memberId, payload.postId);
    }
    catch (error) {
        yield put({ type: types.REMOVE_COMMENT_REACTION_FAILURE, error });
        console.log('remove comment reaction failed', error);
        logUI(types.REMOVE_COMMENT_REACTION_FAILURE, new Date(), tools.getURLPath(), { ...payload, error }, prayerFeature.memberId);
    }
    finally {
        if (payload.callback) {
            payload.callback(success);
        }
    }
}

export function* handleUsersDeletedPosts(payload) {
    let response = null;
    const prayerFeature = yield select(state => state.prayerFeature);
    try {
        logUI(types.GET_USERS_DELETED_POSTS, new Date(), tools.getURLPath(), null, prayerFeature.memberId);
        response = yield call(getUsersDeletedPosts);
    }
    catch (error) {
        yield put({ type: types.GET_USERS_DELETED_POSTS_FAILURE });
        logUI(types.GET_USERS_DELETED_POSTS_FAILURE, new Date(), tools.getURLPath(), null, prayerFeature.memberId);
        console.log(error);
    }
    finally {
        if (payload && payload.callback) {
            payload.callback(response);
        }
    }
}